import React from 'react';
import { Document, Page, View, Text as PDFText, Image, StyleSheet } from '@react-pdf/renderer';
import { Invoice, Customer } from '@the-real-insight/tri-model';
import dayjs from 'dayjs';
import { legalPersonAddressFields, legalPersonCompanyName } from '../utilities/legalPersonDisplay';

const styles = StyleSheet.create({
    logo: {
        width: 200,
    },
    marginTopL: {
        marginTop: 20,
    },
    header1: {
        marginTop: 20,
        marginBottom: 20,
        fontSize: 24,
        fontWeight: 'bold',
        fontFamily: 'Helvetica',
    },
    normal: {
        fontSize: 12,
        fontFamily: 'Helvetica',
        color: '#000000',
    },
    bold: {
        fontSize: 12,
        fontFamily: 'Helvetica',
        fontWeight: 'bold',
        color: '#000000',
    },
    row: {
        flexDirection: 'row',
        marginBottom: 5,
    },
    columnEnd: {
        flexDirection: 'column',
        alignItems: 'flex-end',
    },
    width70: {
        width: '70%',
    },
    width30: {
        width: '30%',
    },
    width40: {
        width: '40%',
    },
    width10: {
        width: '10%',
    },
    width15: {
        width: '15%',
    },
    width20: {
        width: '20%',
    },
    width85: {
        width: '85%',
    },
    textAlignRight: {
        textAlign: 'right',
    },
    address: {
        marginBottom: 10,
    },
    tableContainer: {
        marginTop: 20,
    },
    tableHeaderRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: '#000000',
        borderBottomStyle: 'solid',
        paddingBottom: 5,
        marginBottom: 5,
    },
    itemGroupRow: {
        flexDirection: 'row',
        backgroundColor: '#f0f0f0',
        padding: 5,
        marginBottom: 5,
    },
    totalRow: {
        flexDirection: 'row',
        borderTopWidth: 1,
        borderTopColor: '#000000',
        borderTopStyle: 'solid',
        paddingTop: 5,
        marginTop: 5,
    },
    footerRow: {
        flexDirection: 'row',
        borderTopWidth: 1,
        borderTopColor: '#000000',
        borderTopStyle: 'solid',
        paddingTop: 5,
        marginTop: 5,
    },
    cell: {
        padding: 5,
    },
    valueCell: {
        width: '70%',
        padding: 5,
    },
});

const pdfStyles = StyleSheet.create({
    page: {
        paddingTop: 35,
        paddingBottom: 65,
        paddingHorizontal: 35,
    },
});

const mergeStyles = (...styles: any[]) => {
    return styles.reduce((acc, style) => ({ ...acc, ...style }), {});
};

export const PDFInvoice: React.FC<{ invoice: Invoice, customer: Customer }> = ({ invoice, customer }) => {
    const currencyFormat = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
    const numberFormat = new Intl.NumberFormat('de-DE');
    const amountFormat = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 4, maximumFractionDigits: 4 });
    let pos = 0;

    const sellerAddr = legalPersonAddressFields(invoice.seller as any);
    const buyerAddr = legalPersonAddressFields(invoice.buyer as any);

    try {
        return (
            <Document>
                <Page size="A4" style={pdfStyles.page}>
                    <View style={styles.row}>
                        <View style={styles.width70}>
                            <Image style={styles.logo} src={'/logo.png'} />
                        </View>
                        <View style={styles.width30}>
                            <View style={styles.columnEnd}>
                                <View style={styles.address}>
                                    <PDFText style={styles.bold}>{legalPersonCompanyName(invoice.seller as any)}</PDFText>
                                    <PDFText style={styles.normal}>{sellerAddr.street} {sellerAddr.streetNumber}</PDFText>
                                    <PDFText style={styles.normal}>{sellerAddr.postalCode} {sellerAddr.city}</PDFText>
                                </View>
                            </View>
                        </View>
                    </View>
                    <View style={{ ...styles.row, ...styles.marginTopL }}>
                        <PDFText style={styles.bold}>{legalPersonCompanyName(invoice.buyer as any)}</PDFText>
                    </View>
                    <View style={styles.row}>
                        <PDFText style={styles.normal}>{buyerAddr.street} {buyerAddr.streetNumber}</PDFText>
                    </View>
                    <View style={styles.row}>
                        <PDFText style={styles.normal}>{buyerAddr.postalCode} {buyerAddr.city}</PDFText>
                    </View>
                    <View style={styles.row}>
                        <PDFText style={styles.normal}>UStId DE124356789</PDFText>
                    </View>
                    <PDFText style={styles.header1}>Rechnung {invoice.periodFromDate.getMonth() + 1}/{invoice.periodFromDate.getFullYear()}</PDFText>
                    <View style={styles.row}>
                        <View style={styles.width30}>
                            <PDFText style={styles.bold}>Rechnungsnummer:</PDFText>
                        </View>
                        <View style={styles.valueCell}>
                            <PDFText style={styles.normal}>{invoice.id}</PDFText>
                        </View>
                    </View>
                    <View style={styles.row}>
                        <View style={styles.width30}>
                            <PDFText style={styles.bold}>Kundennummer:</PDFText>
                        </View>
                        <View style={styles.valueCell}>
                            <PDFText style={styles.normal}>{customer.id}</PDFText>
                        </View>
                    </View>
                    <View style={styles.row}>
                        <View style={styles.width30}>
                            <PDFText style={styles.bold}>Abrechnungszeitraum:</PDFText>
                        </View>
                        <View style={styles.width30}>
                            <PDFText style={styles.normal}>
                                {dayjs(invoice.periodFromDate).format('DD.MM.YYYY')} - {dayjs(invoice.periodToDate).format('DD.MM.YYYY')}
                            </PDFText>
                        </View>
                    </View>
                    <View style={{ ...styles.tableContainer, ...styles.marginTopL }}>
                        <View style={styles.tableHeaderRow}>
                            <View style={mergeStyles(styles.width10, styles.cell)}>
                                <PDFText style={mergeStyles(styles.bold, styles.textAlignRight)}>Pos.</PDFText>
                            </View>
                            <View style={mergeStyles(styles.width40, styles.cell)}>
                                <PDFText style={styles.bold}>Beschreibung</PDFText>
                            </View>
                            <View style={mergeStyles(styles.width15, styles.cell)}>
                                <PDFText style={mergeStyles(styles.bold, styles.textAlignRight)}>Menge</PDFText>
                            </View>
                            <View style={mergeStyles(styles.width20, styles.cell)}>
                                <PDFText style={mergeStyles(styles.bold, styles.textAlignRight)}>Betrag</PDFText>
                            </View>
                            <View style={mergeStyles(styles.width15, styles.cell)}>
                                <PDFText style={mergeStyles(styles.bold, styles.textAlignRight)}>Gesamt</PDFText>
                            </View>
                        </View>
                        {invoice.itemGroups.map((itemGroup: any) => (
                            <React.Fragment key={itemGroup.name}>
                                <View style={styles.itemGroupRow}>
                                    <PDFText style={styles.bold}>{itemGroup.name}</PDFText>
                                </View>
                                {itemGroup.items.map((item: any) => {
                                    ++pos;
                                    return (
                                        <View key={pos} style={styles.row}>
                                            <View style={mergeStyles(styles.width10, styles.cell)}>
                                                <PDFText style={mergeStyles(styles.normal, styles.textAlignRight)}>{pos}</PDFText>
                                            </View>
                                            <View style={mergeStyles(styles.width40, styles.cell)}>
                                                <PDFText style={styles.normal}>{item.description}</PDFText>
                                            </View>
                                            <View style={mergeStyles(styles.width15, styles.cell)}>
                                                <PDFText style={mergeStyles(styles.normal, styles.textAlignRight)}>{numberFormat.format(item.volume)}</PDFText>
                                            </View>
                                            <View style={mergeStyles(styles.width20, styles.cell)}>
                                                <PDFText style={mergeStyles(styles.normal, styles.textAlignRight)}>{amountFormat.format(item.amount)}</PDFText>
                                            </View>
                                            <View style={mergeStyles(styles.width15, styles.cell)}>
                                                <PDFText style={mergeStyles(styles.normal, styles.textAlignRight)}>{currencyFormat.format(item.totalAmount)}</PDFText>
                                            </View>
                                        </View>
                                    );
                                })}
                            </React.Fragment>
                        ))}
                        <View style={styles.totalRow}>
                            <View style={mergeStyles(styles.width85, styles.cell)}>
                                <PDFText style={styles.bold}>Gesamtbetrag (netto)</PDFText>
                            </View>
                            <View style={mergeStyles(styles.width15, styles.cell)}>
                                <PDFText style={mergeStyles(styles.bold, styles.textAlignRight)}>{currencyFormat.format(invoice.subtotal())}</PDFText>
                            </View>
                        </View>
                        <View style={styles.row}>
                            <View style={mergeStyles(styles.width85, styles.cell)}>
                                <PDFText style={styles.normal}>MwSt.</PDFText>
                            </View>
                            <View style={mergeStyles(styles.width15, styles.cell)}>
                                <PDFText style={mergeStyles(styles.normal, styles.textAlignRight)}>{currencyFormat.format(invoice.grandTotal(0.19) - invoice.subtotal())}</PDFText>
                            </View>
                        </View>
                        <View style={styles.footerRow}>
                            <View style={mergeStyles(styles.width85, styles.cell)}>
                                <PDFText style={styles.bold}>Gesamtbetrag (brutto)</PDFText>
                            </View>
                            <View style={mergeStyles(styles.width15, styles.cell)}>
                                <PDFText style={mergeStyles(styles.bold, styles.textAlignRight)}>{currencyFormat.format(invoice.grandTotal(0.19))}</PDFText>
                            </View>
                        </View>
                    </View>
                    <View style={mergeStyles(styles.row, styles.marginTopL)}>
                        <PDFText style={styles.normal}>Zahlbar innerhalb von 14 Tagen ohne Abzug.</PDFText>
                    </View>
                    <View style={mergeStyles(styles.row, styles.marginTopL)}>
                        <PDFText style={styles.normal}>Bankverbindung: Konto IBAN 47011100001, Sparkasse Limburg</PDFText>
                    </View>
                </Page>
            </Document>
        );
    } catch (error) {
        console.log(error);
        return (
            <Document>
                <Page>
                    <PDFText>Error in Invoice Creation.</PDFText>
                </Page>
            </Document>
        );
    }
};

export const generateEInvoice = (invoice: Invoice): string => {
    console.log('Invoice >>>', invoice);
    const sellerAddr = legalPersonAddressFields(invoice.seller as any);
    const sellerName = legalPersonCompanyName(invoice.seller as any);
    const buyerName = legalPersonCompanyName(invoice.buyer as any);
    let xml = `<?xml version="1.0" encoding="UTF-8"?>
<ubl:Invoice xmlns:ubl="urn:oasis:names:specification:ubl:schema:xsd:Invoice-2"
             xmlns:cac="urn:oasis:names:specification:ubl:schema:xsd:CommonAggregateComponents-2"
             xmlns:cbc="urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2">
    <cbc:CustomizationID>urn:cen.eu:en16931:2017#compliant#urn:xeinkauf.de:kosit:xrechnung_3.0</cbc:CustomizationID>
    <cbc:ProfileID>urn:fdc:peppol.eu:2017:poacc:billing:01:1.0</cbc:ProfileID>
    <cbc:ID>${invoice.id}</cbc:ID>
    <cbc:IssueDate>${invoice.creationDate}</cbc:IssueDate>
    <cbc:InvoiceTypeCode>380</cbc:InvoiceTypeCode>
    <cbc:Note>#ADU#Wir liefern Ihnen ausschließlich zu unseren allgemeinen Geschäftsbedingungen (Stand Februar 2025). Diese werden Ihnen jederzeit auf Wunsch übersandt. Bei Aufträgen, basierend auf öffentlichen Vergaben, gelten die ggf. vereinbarten abweichenden Liefer- und Zahungsbedingungen des Auftraggebers.</cbc:Note>
    <cbc:DocumentCurrencyCode>EUR</cbc:DocumentCurrencyCode>
    <cbc:BuyerReference>04011000-12350-85</cbc:BuyerReference>
    <cac:OrderReference>
        <cbc:ID>Dummywert</cbc:ID>
        <cbc:SalesOrderID>Auftragsnummer</cbc:SalesOrderID>
    </cac:OrderReference>
    <cac:AccountingSupplierParty>
        <cac:Party>
            <cbc:EndpointID schemeID="EM">invoicing@fireiq.it</cbc:EndpointID>
            <cac:PartyName>
                <cbc:Name>${sellerName}</cbc:Name>
            </cac:PartyName>
            <cac:PostalAddress>
                <cbc:StreetName>${sellerAddr.street} ${sellerAddr.streetNumber}</cbc:StreetName>
                <cbc:CityName>${sellerAddr.city}</cbc:CityName>
                <cbc:PostalZone>${sellerAddr.postalCode}</cbc:PostalZone>
                <cac:Country>
                    <cbc:IdentificationCode>DE</cbc:IdentificationCode>
                </cac:Country>
            </cac:PostalAddress>
            <cac:PartyTaxScheme>
                <cbc:CompanyID>DE123456789</cbc:CompanyID>
                <cac:TaxScheme>
                    <cbc:ID>VAT</cbc:ID>
                </cac:TaxScheme>
            </cac:PartyTaxScheme>
            <cac:PartyLegalEntity>
                <cbc:RegistrationName>${sellerName}</cbc:RegistrationName>
                <cbc:CompanyID>${invoice.seller.tradeRegisterId}</cbc:CompanyID>
                <cbc:CompanyLegalForm>Sitz der Gesellschaft: ${invoice.seller.tradeRegisterCity}, Registergericht: ${invoice.seller.tradeRegisterCity}, Geschäftsführer: </cbc:CompanyLegalForm>
            </cac:PartyLegalEntity>
            <cac:Contact>
                <cbc:Name>Berater / Sachbearbeiter</cbc:Name>
                <cbc:Telephone>+49(0)1234-0</cbc:Telephone>
                <cbc:ElectronicMail>info@seller.de</cbc:ElectronicMail>
            </cac:Contact>
        </cac:Party>
    </cac:AccountingSupplierParty>
    <cac:AccountingCustomerParty>
        <cac:Party>
            <cbc:EndpointID schemeID="EM">judith@null-isiko.de</cbc:EndpointID>
            <cac:PostalAddress>
                <cbc:StreetName>${invoice.billingAddress.street} ${invoice.billingAddress.streetNumber}</cbc:StreetName>
                <cbc:CityName>${invoice.billingAddress.city}</cbc:CityName>
                <cbc:PostalZone>${invoice.billingAddress.postalCode}</cbc:PostalZone>
                <cac:Country>
                    <cbc:IdentificationCode>DE</cbc:IdentificationCode>
                </cac:Country>
            </cac:PostalAddress>
            <cac:PartyLegalEntity>
                <cbc:RegistrationName>${buyerName}</cbc:RegistrationName>
            </cac:PartyLegalEntity>
            <cac:Contact>
                <cbc:Name>Ansprechpartner Käufer</cbc:Name>
            </cac:Contact>
        </cac:Party>
    </cac:AccountingCustomerParty>
    <cac:Delivery>
        <cac:DeliveryLocation>
            <cac:Address>
                <cbc:StreetName>${invoice.billingAddress.street} ${invoice.billingAddress.streetNumber}</cbc:StreetName>
                <cbc:CityName>${invoice.billingAddress.city}</cbc:CityName>
                <cbc:PostalZone>${invoice.billingAddress.postalCode}</cbc:PostalZone>
                <cac:Country>
                    <cbc:IdentificationCode>DE</cbc:IdentificationCode>
                </cac:Country>
            </cac:Address>
        </cac:DeliveryLocation>
        <cac:DeliveryParty>
            <cac:PartyName>
                <cbc:Name>Außenstelle XY</cbc:Name>
            </cac:PartyName>
        </cac:DeliveryParty>
    </cac:Delivery>
    <cac:PaymentMeans>
        <cbc:PaymentMeansCode>58</cbc:PaymentMeansCode>
        <cac:PayeeFinancialAccount>
            <cbc:ID>DE75512108001245126199</cbc:ID>
        </cac:PayeeFinancialAccount>
    </cac:PaymentMeans>
    <cac:PaymentTerms>
        <cbc:Note></cbc:Note>
    </cac:PaymentTerms>
    <cac:TaxTotal>
        <cbc:TaxAmount currencyID="EUR">414.2</cbc:TaxAmount>
        <cac:TaxSubtotal>
            <cbc:TaxableAmount currencyID="EUR">2180</cbc:TaxableAmount>
            <cbc:TaxAmount currencyID="EUR">414.2</cbc:TaxAmount>
            <cac:TaxCategory>
                <cbc:ID>S</cbc:ID>
                <cbc:Percent>19</cbc:Percent>
                <cac:TaxScheme>
                    <cbc:ID>VAT</cbc:ID>
                </cac:TaxScheme>
            </cac:TaxCategory>
        </cac:TaxSubtotal>
    </cac:TaxTotal>
    <cac:LegalMonetaryTotal>
        <cbc:LineExtensionAmount currencyID="EUR">${invoice.subtotal()}</cbc:LineExtensionAmount>
        <cbc:TaxExclusiveAmount currencyID="EUR">2180</cbc:TaxExclusiveAmount>
        <cbc:TaxInclusiveAmount currencyID="EUR">2594.2</cbc:TaxInclusiveAmount>
        <cbc:PayableAmount currencyID="EUR">${invoice.grandTotal(0.19)}</cbc:PayableAmount>
    </cac:LegalMonetaryTotal>`;

    for (const itemGroup of invoice.itemGroups) {
        for (const item of itemGroup.items) {
            xml += `<cac:InvoiceLine>
        <cbc:ID>1</cbc:ID>
        <cbc:InvoicedQuantity unitCode="XPP">${item.volume}</cbc:InvoicedQuantity>
        <cbc:LineExtensionAmount currencyID="EUR">2180</cbc:LineExtensionAmount>
        <cac:Item>
            <cbc:Description>${item.description}</cbc:Description>
            <cbc:Name></cbc:Name>
            <cac:SellersItemIdentification>
                <cbc:ID>AL281982</cbc:ID>
            </cac:SellersItemIdentification>
            <cac:ClassifiedTaxCategory>
                <cbc:ID>S</cbc:ID>
                <cbc:Percent>19</cbc:Percent>
                <cac:TaxScheme>
                    <cbc:ID>VAT</cbc:ID>
                </cac:TaxScheme>
            </cac:ClassifiedTaxCategory>
        </cac:Item>
        <cac:Price>
            <cbc:PriceAmount currencyID="EUR">${item.amount}</cbc:PriceAmount>
        </cac:Price>
    </cac:InvoiceLine>`;
        }
    }

    xml += `</ubl:Invoice>`;

    return xml;
};

export const downloadInvoice = (invoice: Invoice) => {
    const xml = generateEInvoice(invoice);
    const blob = new Blob([xml], { type: 'application/xml' });
    downloadFile(blob, 'Abrechnung.xml');
};

export const downloadFile = (blob: Blob, fileName: string) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
}; 